import type React from 'react';

// Shape returned by the API for any media row. `srcSet` / `sizes` are built
// server-side from the stored responsive set, so the client never has to know
// about CDN paths or which conversion widths exist.
export type MediaRef = {
  path: string;
  url?: string;
  srcSet?: string;
  sizes?: string;
  /** 1200x630 social-card crop, present once the media has been converted. */
  ogUrl?: string;
  ogWidth?: number;
  ogHeight?: number;
  width?: number | null;
  height?: number | null;
  /** Bytes of the original. Used to keep oversized files out of social cards. */
  size?: number | null;
  altText?: string | null;
  blurDataUrl?: string | null;
  dominantColor?: string | null;
} | null;

/**
 * Shown whenever an article has no usable media. It is a 16:9 7KB WebP rather
 * than the 500x500 logo: the fallback lands in card and hero slots, so a square
 * source distorted the layout, and as the LCP element on any lead story without
 * a cover it was the single heaviest asset on the page.
 */
export const FALLBACK_IMG = '/assets/images/article-placeholder.webp';
const FALLBACK_WIDTH = 800;
const FALLBACK_HEIGHT = 450;

export function mediaSrc(media: MediaRef): string {
  return media?.url ?? media?.path ?? FALLBACK_IMG;
}

const onImgError: React.ReactEventHandler<HTMLImageElement> = (e) => {
  const el = e.currentTarget;
  if (el.src.endsWith(FALLBACK_IMG)) return;
  // Drop the responsive candidates too: if the original 404s the derivatives
  // almost certainly did as well, and a stale srcset would win over the fallback.
  el.srcset = '';
  el.src = FALLBACK_IMG;
};

export type SmartImageProps = {
  media: MediaRef;
  alt: string;
  /**
   * Set on the single largest above-the-fold image of a page (the LCP element).
   * Switches off lazy-loading and asks the browser to prioritise the fetch.
   * Never set it on more than one image per page — priority everywhere is
   * priority nowhere.
   */
  priority?: boolean;
  sizes?: string;
  className?: string;
  style?: React.CSSProperties;
};

export function SmartImage({
  media,
  alt,
  priority = false,
  sizes,
  className,
  style,
}: SmartImageProps) {
  const src = mediaSrc(media);
  const isFallback = src === FALLBACK_IMG;
  // Imported legacy rows carry a URL but no dimensions. Without width/height
  // the browser reserves no space and every card below the fold jumps once the
  // image lands, which is exactly what CLS measures. The fallback's own 16:9
  // box is the closest correct guess we have for those rows.
  const width = media?.width ?? (isFallback ? FALLBACK_WIDTH : undefined);
  const height = media?.height ?? (isFallback ? FALLBACK_HEIGHT : undefined);
  const placeholder = media?.blurDataUrl ?? undefined;
  // Legacy rows that have neither stored dimensions nor the fallback's known
  // box get a CSS-declared ratio instead, so space is still reserved up front.
  const ratioClass = width === undefined || height === undefined ? 'smart-img--ratio' : '';
  const classes = [className, ratioClass].filter(Boolean).join(' ') || undefined;

  return (
    <img
      src={src}
      srcSet={media?.srcSet}
      sizes={media?.srcSet ? (sizes ?? media.sizes) : undefined}
      alt={media?.altText || alt}
      width={width}
      height={height}
      loading={priority ? 'eager' : 'lazy'}
      // `sync` on the LCP image avoids the extra frame async decoding costs.
      decoding={priority ? 'sync' : 'async'}
      fetchPriority={priority ? 'high' : undefined}
      className={classes}
      style={
        placeholder
          ? {
              backgroundImage: `url("${placeholder}")`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              ...style,
            }
          : style
      }
      onError={onImgError}
    />
  );
}
